import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../../services/auth';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useTimezone } from '../../../contexts/TimezoneContext';
import './AppLayout.css';

const AppLayout = ({ 
    title,
    children,
    error,
    setError,
    actions = null,
    onLogout
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { resetToLocalLanguage } = useLanguage();
    const { resetToLocalTimezone } = useTimezone();
    const user = authService.getCurrentUser();

    const handleLogout = async () => {
        await authService.logout();
        
        // Reset language and timezone to local preferences
        resetToLocalLanguage();
        resetToLocalTimezone();
        
        if (onLogout) {
            onLogout();
        }
        navigate('/login');
    };


    const navigationItems = [
        { path: '/', label: t('common:navigation.dashboard'), icon: '🏠' },
        { path: '/users', label: t('common:navigation.users'), icon: '👥', adminOnly: true },
        { path: '/inventory', label: t('common:navigation.inventory'), icon: '📦' }
    ];

    const filteredNavigationItems = navigationItems.filter(item => 
        !item.adminOnly || (item.adminOnly && user?.role === 'admin')
    );

    return (
        <div className="app-layout">
            <header className="app-header">
                <div className="header-content">
                    <div className="header-left">
                        <h1 className="app-title">
                            {title}
                        </h1>
                    </div>
                    <div className="header-right">
                        {actions && <div className="header-actions">{actions}</div>}
                        <div className="user-section">
                            <div className="user-info">
                                <Link 
                                    to="/profile" 
                                    className="user-name-link"
                                    title={t('common:navigation.profile')}
                                >
                                    <span className="user-name">
                                        {user?.username}
                                    </span>
                                </Link>
                                <span className="user-role">
                                    {user?.role}
                                </span>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="logout-button"
                            >
                                {t('common:buttons.logout')}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <nav className="app-navigation">
                <div className="nav-content">
                    {filteredNavigationItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                        >
                            <span className="nav-icon">{item.icon}</span>
                            <span className="nav-label">{item.label}</span>
                        </Link>
                    ))}
                </div>
            </nav>

            <main className="app-main">
                {/* Error Message */}
                {error && (
                    <div className="error-banner">
                        <span>{error}</span>
                        {setError && (
                            <button 
                                onClick={() => setError('')}
                                className="error-close"
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}

                {/* Page Content */}
                <div className="page-content">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default AppLayout;